const DAY_IN_MS = 24 * 60 * 60 * 1000;

function padDateUnit(value: number) {
  return String(value).padStart(2, "0");
}

export function normalizeDateValue(value: Date | string) {
  if (typeof value === "string") {
    const matchedDate = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (matchedDate) {
      const [, year, month, day] = matchedDate;
      const nextDate = new Date(Number(year), Number(month) - 1, Number(day));
      nextDate.setHours(12, 0, 0, 0);
      return nextDate;
    }
  }

  const nextDate = new Date(value);
  nextDate.setHours(12, 0, 0, 0);
  return nextDate;
}

export function formatDateInputValue(value: Date) {
  const date = normalizeDateValue(value);

  return `${date.getFullYear()}-${padDateUnit(date.getMonth() + 1)}-${padDateUnit(date.getDate())}`;
}

export function parseDateInputValue(value: string) {
  return normalizeDateValue(value);
}

export function addDaysToDate(value: Date | string, days: number) {
  const nextDate = normalizeDateValue(value);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
}

export function getCalendarDaySpan(startDate: Date, endDate: Date) {
  const start = normalizeDateValue(startDate);
  const end = normalizeDateValue(endDate);

  return Math.max(0, Math.round((end.getTime() - start.getTime()) / DAY_IN_MS));
}

export function formatDisplayDate(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(normalizeDateValue(value));
}

export function formatCompactDate(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "short",
  }).format(normalizeDateValue(value));
}

export function getFullDayEventRange(
  startValue: Date | string,
  endValue: Date | string,
) {
  const start = normalizeDateValue(startValue);
  const end = normalizeDateValue(endValue);

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  return { start, end };
}

export interface EventDayWindowItem {
  section_date: string;
  window_start: string;
  window_end: string;
}

export function setTimeOnDate(
  value: Date | string,
  timeValue: string,
  dayOffset = 0,
) {
  const nextDate = normalizeDateValue(value);
  const [hours, minutes] = timeValue.split(":").map((part) => Number(part));

  nextDate.setHours(
    Number.isFinite(hours) ? hours : 0,
    Number.isFinite(minutes) ? minutes : 0,
    0,
    0,
  );
  nextDate.setDate(nextDate.getDate() + dayOffset);

  return nextDate;
}

export function buildEventWindowRanges(dayWindows: EventDayWindowItem[]) {
  return dayWindows.map((window) => {
    const start = setTimeOnDate(window.section_date, window.window_start);
    const crossesMidnight = window.window_end <= window.window_start;
    const end = setTimeOnDate(
      window.section_date,
      window.window_end,
      crossesMidnight ? 1 : 0,
    );

    return { end, start };
  });
}

export function formatTimeInputValue(value: Date | string) {
  const date = new Date(value);

  return `${padDateUnit(date.getHours())}:${padDateUnit(date.getMinutes())}`;
}

export function formatDisplayTime(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

/**
 * Wall-clock time (`HH:MM`, 24h) of an absolute instant in the viewer's local
 * timezone. Timestamps are stored as UTC instants, so the raw ISO `HH:MM` is
 * offset from the local time the role was authored in — this renders the
 * instant back to local wall-clock, like the rest of the planning UI.
 */
export function formatLocalTime(value: Date | string, locale?: string) {
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

/** Local calendar day (`YYYY-MM-DD`) of an absolute instant. */
export function formatLocalDayKey(value: Date | string) {
  const date = new Date(value);

  return `${date.getFullYear()}-${padDateUnit(date.getMonth() + 1)}-${padDateUnit(date.getDate())}`;
}
